import { requestApiJson } from '../../shared/api/client'
import { withCookieFirstRequest } from '../../shared/api/cookieAuthRequest'
import type {
  CheckoutConfirmEnvelope,
  CheckoutConfirmResult,
  CheckoutReviewEnvelope,
  CheckoutSession,
  CheckoutSessionEnvelope,
  ConfirmCheckoutSessionInput,
  CreateCheckoutSessionInput,
  SaveBillingAddressInput,
  SaveShippingAddressInput,
  SaveShippingMethodInput,
} from '../types'

const toErrorMessage = (status: number, payload: unknown) => {
  if (payload && typeof payload === 'object') {
    const errorCode = (payload as { error?: unknown }).error
    if (typeof errorCode === 'string') {
      if (errorCode === 'insufficient_stock') return 'Uno o più prodotti non sono più disponibili.'
      if (errorCode === 'availability_changed') return 'La disponibilità del carrello è cambiata.'
      if (errorCode === 'sold_out') return 'Questo prodotto è terminato durante il checkout.'
      if (errorCode === 'duplicate_checkout') return 'La disponibilità del carrello è cambiata.'
    }

    const message = (payload as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) {
      return message.trim()
    }
  }

  if (status === 400) return 'Dati checkout non validi'
  if (status === 401) return 'Sessione cliente non valida'
  if (status === 403) return 'Permessi insufficienti per checkout'
  if (status === 404) return 'Checkout session non trovata'
  if (status === 409) return 'Checkout non disponibile nello stato attuale'
  if (status === 503) return 'Servizio checkout temporaneamente non disponibile'
  if (status === 504) return 'Timeout servizio checkout'
  return 'Errore checkout'
}

export class CheckoutApiError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

export const createCheckoutSession = async ({
  token,
  cartId,
  successPath,
  cancelPath,
}: CreateCheckoutSessionInput): Promise<CheckoutSession> => {
  const payload = await requestApiJson<CheckoutSessionEnvelope>({
    path: '/payments/sessions',
    init: withCookieFirstRequest({
      init: {
        method: 'POST',
        body: JSON.stringify({ cartId, successPath, cancelPath }),
      },
      bearerFallbackToken: token,
    }),
    toErrorMessage,
    createError: (status, message) => new CheckoutApiError(status, message),
  })

  return payload.session
}

export const getCheckoutSession = async ({
  token,
  id,
}: {
  token?: string | null
  id: string
}): Promise<CheckoutSession> => {
  const payload = await requestApiJson<CheckoutSessionEnvelope>({
    path: `/payments/sessions/${encodeURIComponent(id)}`,
    init: withCookieFirstRequest({
      init: {
        method: 'GET',
      },
      bearerFallbackToken: token,
    }),
    toErrorMessage,
    createError: (status, message) => new CheckoutApiError(status, message),
  })

  return payload.session
}

export const saveShippingAddress = async ({ token, id, payload }: SaveShippingAddressInput): Promise<CheckoutSession> => {
  const response = await requestApiJson<CheckoutSessionEnvelope>({
    path: `/payments/sessions/${encodeURIComponent(id)}/shipping-address`,
    init: withCookieFirstRequest({
      init: {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
      bearerFallbackToken: token,
    }),
    toErrorMessage,
    createError: (status, message) => new CheckoutApiError(status, message),
  })

  return response.session
}

export const saveBillingAddress = async ({ token, id, payload }: SaveBillingAddressInput): Promise<CheckoutSession> => {
  const response = await requestApiJson<CheckoutSessionEnvelope>({
    path: `/payments/sessions/${encodeURIComponent(id)}/billing-address`,
    init: withCookieFirstRequest({
      init: {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
      bearerFallbackToken: token,
    }),
    toErrorMessage,
    createError: (status, message) => new CheckoutApiError(status, message),
  })

  return response.session
}

export const saveShippingMethod = async ({ token, id, payload }: SaveShippingMethodInput): Promise<CheckoutSession> => {
  const response = await requestApiJson<CheckoutSessionEnvelope>({
    path: `/payments/sessions/${encodeURIComponent(id)}/shipping-method`,
    init: withCookieFirstRequest({
      init: {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
      bearerFallbackToken: token,
    }),
    toErrorMessage,
    createError: (status, message) => new CheckoutApiError(status, message),
  })

  return response.session
}

export const getCheckoutReview = async ({
  token,
  id,
}: {
  token?: string | null
  id: string
}): Promise<CheckoutReviewEnvelope> => {
  return requestApiJson<CheckoutReviewEnvelope>({
    path: `/payments/sessions/${encodeURIComponent(id)}/review`,
    init: withCookieFirstRequest({
      init: {
        method: 'GET',
      },
      bearerFallbackToken: token,
    }),
    toErrorMessage,
    createError: (status, message) => new CheckoutApiError(status, message),
  })
}

export const confirmCheckoutSession = async ({
  token,
  id,
  payload,
}: ConfirmCheckoutSessionInput): Promise<CheckoutConfirmResult> => {
  return requestApiJson<CheckoutConfirmEnvelope>({
    path: `/payments/sessions/${encodeURIComponent(id)}/confirm`,
    init: withCookieFirstRequest({
      init: {
        method: 'POST',
        body: JSON.stringify({
          cartId: payload?.cartId,
          successPath: payload?.successPath,
          cancelPath: payload?.cancelPath,
          paymentProvider: payload?.paymentProvider,
        }),
      },
      bearerFallbackToken: token,
    }),
    toErrorMessage,
    createError: (status, message) => new CheckoutApiError(status, message),
  })
}
